import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import Plot from 'react-plotly.js';
import { useNavigate } from 'react-router-dom';
import { Mountain, AlertCircle, CheckCircle, AlertTriangle } from 'lucide-react';
import { API } from '../App';

const COVERAGE_STYLES = {
  full_grid: { bg: '#ECFDF5', border: '#10B981', text: '#065F46', icon: CheckCircle },
  mostly_full: { bg: '#FFFBEB', border: '#F59E0B', text: '#92400E', icon: AlertTriangle },
  sparse: { bg: '#FEF2F2', border: '#DC2626', text: '#991B1B', icon: AlertCircle },
};

export default function ResponseSurface3D() {
  const navigate = useNavigate();
  const [history, setHistory] = useState([]);
  const [aId, setAId] = useState('');
  const [trait, setTrait] = useState('');
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    axios.get(`${API}/analysis-history`, { withCredentials: true })
      .then(r => {
        if (mounted) {
          // Only keep factorial-shaped designs in the picker (anything
          // that surfaces a 2-factor cell-means table).
          const eligible = (r.data || []).filter(h => {
            const t = (h.analysis_type || '').toLowerCase();
            return t.includes('split') || t.includes('strip')
              || t.includes('factorial');
          });
          setHistory(eligible);
        }
      })
      .catch(() => { if (mounted) setHistory([]); })
      .finally(() => { if (mounted) setLoadingHistory(false); });
    return () => { mounted = false; };
  }, []);

  const run = async () => {
    if (!aId) {
      setError('Pick an analysis.');
      return;
    }
    setRunning(true);
    setResult(null);
    setError(null);
    try {
      const url = `${API}/analyses/${aId}/response-surface${trait ? `?trait=${encodeURIComponent(trait)}` : ''}`;
      const r = await axios.get(url, { withCredentials: true });
      setResult(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || String(e));
    } finally {
      setRunning(false);
    }
  };

  const plotData = useMemo(() => {
    if (!result) return null;
    return [{
      type: 'surface',
      x: result.y_levels,  // Plotly: x = columns of z_grid
      y: result.x_levels,
      z: result.z_grid,
      colorscale: 'Viridis',
      contours: {
        z: { show: true, usecolormap: true, highlightcolor: '#42f462', project: { z: true } }
      },
      colorbar: { title: result.trait },
    }];
  }, [result]);

  const plotLayout = useMemo(() => {
    if (!result) return null;
    return {
      autosize: true,
      height: 600,
      margin: { l: 20, r: 20, t: 30, b: 20 },
      scene: {
        xaxis: { title: result.y_factor_name },
        yaxis: { title: result.x_factor_name },
        zaxis: { title: result.trait },
        camera: { eye: { x: 1.5, y: 1.5, z: 1.2 } },
      },
    };
  }, [result]);

  const coverageStyle = result ? COVERAGE_STYLES[result.coverage_verdict] || COVERAGE_STYLES.mostly_full : null;
  const CoverageIcon = coverageStyle?.icon;

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg-cream, #F5F1E8)' }} data-testid="response-surface-page">
      <div className="max-w-6xl mx-auto px-6 py-8">
        <button
          onClick={() => navigate(-1)}
          className="text-sm mb-4 text-stone-600 hover:text-stone-900"
          data-testid="back-button"
        >
          ← Back
        </button>
        <div className="flex items-center gap-3 mb-2">
          <Mountain size={28} style={{ color: '#2D6A4F' }} />
          <h1 className="text-3xl font-bold" style={{ color: '#1B4F47' }}>
            Response Surface 3D
          </h1>
        </div>
        <p className="text-stone-600 mb-8 text-sm">
          Interactive 3D surface of the two-factor cell-mean grid. Use this for Split-Plot, Strip-Plot,
          and RBD Factorial designs. Citations: Box &amp; Wilson 1951; Myers et al. 2016.
        </p>

        <div className="bg-white rounded-xl border border-stone-200 p-6 mb-6 shadow-sm">
          <h2 className="font-semibold text-stone-800 mb-4">Select analysis</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-stone-600 mb-2">Factorial Analysis</label>
              <select
                value={aId}
                onChange={(e) => setAId(e.target.value)}
                className="w-full border border-stone-300 rounded-lg px-3 py-2 text-sm"
                data-testid="select-analysis"
                disabled={loadingHistory}
              >
                <option value="">
                  {loadingHistory ? 'Loading...' : history.length === 0 ? 'No eligible analyses' : '— pick one —'}
                </option>
                {history.map(h => (
                  <option key={h.id} value={h.id}>
                    {h.filename} · {h.analysis_type} · {new Date(h.created_at).toLocaleDateString()}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-stone-600 mb-2">Trait (optional)</label>
              <input
                type="text"
                placeholder="leave empty for first trait"
                value={trait}
                onChange={(e) => setTrait(e.target.value)}
                className="w-full border border-stone-300 rounded-lg px-3 py-2 text-sm"
                data-testid="trait-input"
              />
            </div>
          </div>
          <button
            onClick={run}
            disabled={running || !aId}
            className="mt-4 px-6 py-2 rounded-lg font-semibold text-white disabled:opacity-50"
            style={{ background: '#2D6A4F' }}
            data-testid="render-surface-button"
          >
            {running ? 'Rendering...' : 'Render Surface'}
          </button>
        </div>

        {error && (
          <div className="bg-rose-50 border border-rose-300 text-rose-800 p-4 rounded-lg mb-6" data-testid="surface-error">
            <strong>Surface unavailable: </strong>{error}
          </div>
        )}

        {result && (
          <>
            <div
              className="rounded-xl p-4 mb-6 border-2"
              style={{ background: coverageStyle.bg, borderColor: coverageStyle.border }}
              data-testid="coverage-banner"
            >
              <div className="flex items-start gap-3">
                <CoverageIcon size={22} style={{ color: coverageStyle.border, flexShrink: 0 }} />
                <div className="text-sm" style={{ color: coverageStyle.text }}>
                  <strong>Grid coverage:</strong> {result.coverage_pct}% ({result.n_cells_filled}/{result.n_cells_full} cells filled).
                  {' '}{result.coverage_note}
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-stone-200 p-4 mb-6 shadow-sm">
              <div className="text-sm font-semibold text-stone-800 mb-2">
                {result.design_label} · Trait: {result.trait}
              </div>
              <Plot
                data={plotData}
                layout={plotLayout}
                style={{ width: '100%' }}
                useResizeHandler
                config={{ displaylogo: false, responsive: true }}
                data-testid="surface-plot"
              />
            </div>

            <div className="text-xs text-stone-500 italic">
              Methodology: {result.citations.join(' · ')}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
